/**
 * PageDrillPanel - Right-side slide-out panel for page analytics deep dive
 *
 * Shows content role, performance indicators, visitor flow, and AI analysis
 * for a selected website page on the Monarch hotel dashboard.
 *
 * Style: Solid colors only. NO gradients, NO glows, NO glassmorphism.
 * Uses bg-bg-1, border-border, font-mono for metrics.
 */

import { useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  X,
  FileText,
  Activity,
  ArrowRightLeft,
  MessageSquare,
} from 'lucide-react';
import { formatNumber, formatPercentRaw } from '@/lib/formatting';
import { useChatStore, PANEL_WIDTH_PX } from '@/stores/chatStore';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PageData {
  page: string;
  views: number;
}

interface PageDrillPanelProps {
  pageData: PageData | null;
  open: boolean;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TOTAL_PAGEVIEWS = 266_000;
const TOTAL_SESSIONS = 87_615;

const CONTENT_ROLES: Record<string, string> = {
  Homepage: 'Primary entry point — 76% of all sessions start here',
  Dining: 'Gateway content — users exploring F&B before booking',
  Aleteo: 'Signature restaurant — high engagement driver',
  'Oak & Amber': 'Bar & lounge — social discovery content',
  Amenities: 'Property features — mid-funnel consideration',
  Accommodations: 'Room details — bottom-funnel booking intent',
  'Nectarie Cafe': 'Casual dining — supporting content',
};

const CHANNEL_MIX = [
  { channel: 'Paid Social', pct: 33.2, color: '#a855f7' },
  { channel: 'Organic Search', pct: 25.3, color: '#34a853' },
  { channel: 'Direct', pct: 17.4, color: '#71717a' },
  { channel: 'Paid Search', pct: 13.3, color: '#00a3e0' },
  { channel: 'Organic Social', pct: 5.0, color: '#f59e0b' },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SectionHeader({
  icon: Icon,
  title,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-text-3" />
      <h4 className="text-xs font-semibold uppercase tracking-wider text-text-3">
        {title}
      </h4>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PageDrillPanel component
// ---------------------------------------------------------------------------

export default function PageDrillPanel({
  pageData,
  open,
  onClose,
}: PageDrillPanelProps) {
  const btnRef = useRef<HTMLButtonElement>(null);

  // Close on Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, handleKeyDown]);

  const chatIsOpen = useChatStore((s) => s.isOpen);
  const chatPanelWidth = useChatStore((s) => s.panelWidth);
  const rightOffset = chatIsOpen ? PANEL_WIDTH_PX[chatPanelWidth] : 0;

  if (!open) return null;

  // Derived metrics
  const views = pageData?.views ?? 0;
  const share = ((views / TOTAL_PAGEVIEWS) * 100).toFixed(1);
  const viewsPerSession = (views / TOTAL_SESSIONS).toFixed(2);
  const contentRole = pageData ? CONTENT_ROLES[pageData.page] ?? null : null;
  const isBookingIntent =
    pageData?.page === 'Accommodations' ||
    (pageData?.page ?? '').toLowerCase().includes('booking');

  // Rank helper — not calculated from a list, so we derive from share %
  const rankFromShare = (pct: number): number => {
    if (pct >= 30) return 1;
    if (pct >= 15) return 2;
    if (pct >= 8) return 3;
    if (pct >= 5) return 4;
    if (pct >= 3) return 5;
    if (pct >= 1) return 6;
    return 7;
  };
  const rank = rankFromShare(parseFloat(share));

  // AI query
  const handleAskAI = () => {
    if (!pageData || !btnRef.current) return;
    const query = `Analyze the ${pageData.page} page: ${formatNumber(pageData.views)} views (${share}% of total). What content optimizations could improve engagement and drive more bookings?`;
    const rect = btnRef.current.getBoundingClientRect();
    useChatStore
      .getState()
      .showAnalyzePrompt(query, `Page drill: ${pageData.page}`, rect);
  };

  return (
    <>
      {/* Backdrop */}
      <motion.div
        className="fixed inset-0 z-40 bg-black/70"
        style={rightOffset > 0 ? { right: rightOffset } : undefined}
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        aria-hidden
      />

      {/* Slide-out panel */}
      <motion.div
        className="fixed top-0 z-50 h-full w-full sm:max-w-2xl bg-bg-0 border-l border-border"
        style={{ right: rightOffset }}
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        role="dialog"
        aria-modal
        aria-label={pageData ? `${pageData.page} page drill-down` : 'Page details'}
      >
        {/* Sticky Header */}
        <div className="sticky top-0 flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-border bg-bg-1">
          <h2 className="text-lg font-display text-text truncate">
            {pageData?.page ?? 'Page Details'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-none bg-bg-2 border border-border hover:border-border-2 transition-colors text-text-3 hover:text-text"
            aria-label="Close panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-[calc(100%-60px)] px-4 sm:px-6 py-4 sm:py-6 space-y-6 sm:space-y-8">
          {!pageData ? (
            <p className="text-sm text-text-3 italic">No page selected.</p>
          ) : (
            <>
              {/* Hero Metrics */}
              <section>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-bg-2 border border-border rounded-lg p-3">
                    <div className="text-xs text-text-3 uppercase tracking-wider">
                      Pageviews
                    </div>
                    <div className="text-lg font-mono font-bold text-text mt-1">
                      {formatNumber(views)}
                    </div>
                  </div>
                  <div className="bg-bg-2 border border-border rounded-lg p-3">
                    <div className="text-xs text-text-3 uppercase tracking-wider">
                      Share of Total
                    </div>
                    <div className="text-lg font-mono font-bold text-text mt-1">
                      {formatPercentRaw(parseFloat(share))}
                    </div>
                  </div>
                  <div className="bg-bg-2 border border-border rounded-lg p-3">
                    <div className="text-xs text-text-3 uppercase tracking-wider">
                      Views / Session
                    </div>
                    <div className="text-lg font-mono font-bold text-text mt-1">
                      {viewsPerSession}
                    </div>
                  </div>
                </div>
              </section>

              {/* Content Role */}
              {contentRole && (
                <section>
                  <SectionHeader icon={FileText} title="Content Role" />
                  <div className="bg-monarch-gold/5 border border-monarch-gold/20 rounded-lg p-3">
                    <p className="text-sm text-text-2">{contentRole}</p>
                  </div>
                </section>
              )}

              {/* Performance Indicators */}
              <section>
                <SectionHeader icon={Activity} title="Performance Indicators" />
                <div className="space-y-4">
                  {/* Share bar */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-text-2">Share of total pageviews</span>
                      <span className="font-mono text-text">{share}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-bg-2 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-monarch-gold transition-all"
                        style={{ width: `${Math.min(parseFloat(share), 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Ranking */}
                  <div className="flex items-center gap-2 text-sm text-text-2">
                    <span className="font-mono font-bold text-text">#{rank}</span>
                    <span>most-viewed page</span>
                  </div>

                  {/* Booking intent badge */}
                  {isBookingIntent && (
                    <div className="inline-flex items-center px-2.5 py-1 rounded bg-monarch-gold/10 border border-monarch-gold/25">
                      <span className="text-xs font-semibold text-monarch-gold uppercase tracking-wider">
                        Booking Intent Signal
                      </span>
                    </div>
                  )}
                </div>
              </section>

              {/* Visitor Flow */}
              <section>
                <SectionHeader icon={ArrowRightLeft} title="Visitor Flow" />
                <p className="text-xs text-text-3 mb-3">
                  Estimated channel mix for this page
                </p>
                <div className="space-y-2.5">
                  {CHANNEL_MIX.map((ch) => (
                    <div key={ch.channel} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-text-2">{ch.channel}</span>
                        <span className="font-mono text-text-3">
                          {formatPercentRaw(ch.pct)}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-bg-2 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${(ch.pct / CHANNEL_MIX[0].pct) * 100}%`,
                            backgroundColor: ch.color,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Ask AI button */}
              <section className="pb-4">
                <button
                  ref={btnRef}
                  onClick={handleAskAI}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-bg-2 border border-border hover:border-monarch-gold/40 transition-colors text-sm text-text-2 hover:text-text"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span>Ask AI about this page</span>
                </button>
              </section>
            </>
          )}
        </div>
      </motion.div>
    </>
  );
}
